/* Asset labels.
 *
 * /a/:tag has resolved since step 8 — scan a label, land on the device — and
 * nothing has ever generated a label, so the feature has been half-built and
 * unusable for months. This is the other half.
 *
 * The QR encodes an address on this origin rather than a hard-coded domain.
 * Older notes here name ledger.hird.co.uk, which was never created — the custom
 * domain is blueprint.hird.co.uk. A label printed against a domain that does not
 * resolve is worse than no label, because it is stuck to a laptop and looks like
 * it works. Whatever address you are reading this on is the one that goes on the
 * sticker, which is why this reads the origin rather than trusting a note.
 *
 * Sized for 50 × 25 mm, which is a common thermal label and about the smallest
 * a QR survives being scanned from a metre away at. The asset tag is printed
 * underneath in plain text as well, because a camera fails and a person reading
 * a number does not.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useDepots } from '../../hooks/useDepots';
import { useDepotScope, scoped } from '../../hooks/useDepotScope';
import type { IDevice } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import { Button, Controls, EmptyState, Panel, SearchBox, Select, TableSkeleton } from '../ui';
import s from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

/** The short form on the sticker. Kept short deliberately — every character is
    another module in the QR, and a denser code is a code that needs a better
    camera and better light than a comms room has. */
function labelUrl(tag: string): string {
  return `${window.location.origin}/a/${encodeURIComponent(tag)}`;
}

function Label({ device }: { device: IDevice }) {
  const [svg, setSvg] = useState('');

  useEffect(() => {
    let live = true;
    /* M correction, not H. Higher correction means more modules for the same
       address, and a label this size is limited by how small a module can be
       printed and still scan — a torn sticker is rarer than a bad scan. */
    QRCode.toString(labelUrl(device.tag), {
      type: 'svg',
      margin: 0,
      errorCorrectionLevel: 'M',
    })
      .then((out) => {
        if (live) setSvg(out);
      })
      .catch(() => {
        if (live) setSvg('');
      });
    return () => {
      live = false;
    };
  }, [device.tag]);

  return (
    <div className={s.label}>
      <div className={s.labelCode} dangerouslySetInnerHTML={{ __html: svg }} />
      <div className={s.labelText}>
        <strong>{device.tag}</strong>
        <span>{PRODUCT.itTeam} · scan to view</span>
      </div>
    </div>
  );
}

export function LabelsScreen() {
  const svc = useServices();
  const depots = useDepots();
  const { scope } = useDepotScope();

  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });

  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scoped(devices.data ?? [], scope)
      .filter((d) => (type === 'all' ? true : d.type === type))
      .filter((d) => !q || d.tag.toLowerCase().includes(q) || `${d.make} ${d.model}`.toLowerCase().includes(q))
      .sort((a, b) => a.tag.localeCompare(b.tag));
  }, [devices.data, scope, type, query]);

  const types = useMemo(
    () => [...new Set((devices.data ?? []).map((d) => d.type))].sort(),
    [devices.data],
  );

  const chosen = rows.filter((d) => picked.has(d.id));

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <div className={`${s.head} ${s.noPrint}`}>
        <div className={s.grow}>
          <h1>Asset labels</h1>
          <p>
            A QR code that opens the device in {PRODUCT.name}, with the asset tag underneath.
            Sized for 50 × 25 mm.
          </p>
        </div>
        <Button
          primary
          icon="ti-printer"
          onClick={() => window.print()}
          disabled={chosen.length === 0}
        >
          Print {chosen.length > 0 ? `${chosen.length} label${chosen.length === 1 ? '' : 's'}` : 'labels'}
        </Button>
      </div>

      <div className={`${s.ownedNote} ${s.noPrint}`}>
        <i className="ti ti-info-circle" aria-hidden />
        <span>
          The code points at <code>{window.location.origin}/a/…</code> — whatever address you are
          reading this on. If {PRODUCT.name} later moves to a custom domain, labels printed today keep
          working only while this one does, so reprint then.
        </span>
      </div>

      <Controls count={`${chosen.length} of ${rows.length} selected`}>
        <div className={s.noPrint}>
          <SearchBox value={query} onChange={setQuery} placeholder="Asset tag, make or model" />
        </div>
        <Select
          label="Type"
          value={type}
          onChange={setType}
          options={[{ value: 'all', label: 'All types' }, ...types.map((t) => ({ value: t, label: t }))]}
        />
        <Button icon="ti-checkbox" onClick={() => setPicked(new Set(rows.map((d) => d.id)))}>
          Select all shown
        </Button>
        <Button icon="ti-square" onClick={() => setPicked(new Set())}>
          Clear
        </Button>
      </Controls>

      {devices.isLoading ? (
        <Panel flush>
          <TableSkeleton rows={8} />
        </Panel>
      ) : (
        <>
          <Panel title="Choose what to label" flush>
            <div className={`${s.rows} ${s.noPrint}`}>
              {rows.map((d) => (
                <label key={d.id} className={`${s.row} ${s.rowFlush}`}>
                  <input
                    type="checkbox"
                    checked={picked.has(d.id)}
                    onChange={() => toggle(d.id)}
                    aria-label={`Label ${d.tag}`}
                  />
                  <div className={s.rowMain}>
                    <div className={s.rowTitle}>{d.tag}</div>
                    <div className={s.rowSub}>
                      {d.make} {d.model} · {d.type} · {depots.find((x) => x.id === d.loc)?.name ?? d.loc}
                    </div>
                  </div>
                </label>
              ))}
              {rows.length === 0 && (
                <EmptyState icon="ti-qrcode" title="No devices match that" />
              )}
            </div>
          </Panel>

          {/* The sheet. On screen it is a preview; printing hides everything
              else on the page and this is what comes out. */}
          <div className={s.sheet}>
            {chosen.map((d) => (
              <Label key={d.id} device={d} />
            ))}
          </div>
        </>
      )}
    </>
  );
}
